// src/voice/VoiceClient.js

import io from "socket.io-client";
import * as fun from "@/plugins/common";

export default class VoiceClient {
    constructor() {
        this.socket = null;
        this.audioCtx = null;
        this.micStream = null;
        this.processor = null;
        this.running = false;
    }

    // -----------------------------
    // CONNECT TO SERVER
    // -----------------------------
    start() {
        if (this.running) return;
        this.running = true;

        const wsURL = fun.getServerURL().replace("http", "ws") + "/voice";

        console.log("Connecting to:", wsURL);

        this.socket = io(wsURL, {
            transports: ["websocket"],
        });

        this.socket.on("connect", () => {
            console.log("🎤 Voice socket connected");
            this.startMic();
        });

        this.socket.on("connect_error", (err) => {
            console.error("Socket connect error:", err);
        });

        // Receive TTS audio from backend
        this.socket.on("audio_out", (b64) => {
            this.playAudio(b64);
        });
    }

    // -----------------------------
    // MICROPHONE STREAM CAPTURE
    // -----------------------------
    async startMic() {
        try {
            console.log("Microphone started");

            this.micStream = await navigator.mediaDevices.getUserMedia({ audio: true });

            this.audioCtx = new AudioContext({ sampleRate: 16000 });

            const source = this.audioCtx.createMediaStreamSource(this.micStream);

            this.processor = this.audioCtx.createScriptProcessor(2048, 1, 1);
            source.connect(this.processor);
            this.processor.connect(this.audioCtx.destination);

            this.processor.onaudioprocess = (e) => {
                const input = e.inputBuffer.getChannelData(0);

                // Convert Float32 → PCM16
                const pcm16 = new Int16Array(input.length);
                for (let i = 0; i < input.length; i++) {
                    pcm16[i] = Math.max(-1, Math.min(1, input[i])) * 32767;
                }

                // BASE64 encode and send using Socket.IO
                const b64 = btoa(
                    String.fromCharCode(...new Uint8Array(pcm16.buffer))
                );

                if (this.socket && this.socket.connected) {
                    this.socket.emit("voice_stream", b64);
                }
            };
        } catch (error) {
            console.error("Mic error:", error);
        }
    }

    // -----------------------------
    // PLAY AUDIO RETURNED FROM SERVER
    // -----------------------------
    playAudio(b64) {
        try {
            const wavBytes = Uint8Array.from(atob(b64), (c) =>
                c.charCodeAt(0)
            ).buffer;

            if (!this.audioCtx) {
                this.audioCtx = new AudioContext();
            }

            this.audioCtx.decodeAudioData(wavBytes, (decoded) => {
                const src = this.audioCtx.createBufferSource();
                src.buffer = decoded;
                src.connect(this.audioCtx.destination);
                src.start(0);
            });
        } catch (e) {
            console.error("Playback error:", e);
        }
    }

    // -----------------------------
    // STOP EVERYTHING
    // -----------------------------
    stop() {
        this.running = false;

        if (this.processor) this.processor.disconnect();
        if (this.micStream)
            this.micStream.getTracks().forEach((t) => t.stop());

        if (this.socket) {
            this.socket.disconnect();
            this.socket = null;
        }

        if (this.audioCtx) {
            this.audioCtx.close();
            this.audioCtx = null;
        }

        console.log("🎤 Voice stopped");
    }
}
